import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useHtClient } from '../providers/ht';

export interface LeadDetails {
  id: number;
  created_at: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  sf_account?: string | null;
}

export interface Subscription {
  id: number;
  customer_id: string;
  subscription_id: string;
  quiz_id: number;
  status: string;
  current_period_start?: string | null;
  current_period_end?: string | null;
  created_at: string;
  updated_at: string;
  session_id: string;
  email: string;
  cancel_at_period_end: boolean;
  cancelled_at?: string | null;
  lead_details?: LeadDetails | null;
}

export interface SubscriptionCreate {
  customer_id: string;
  subscription_id: string;
  quiz_id: number;
  status: string;
  current_period_start: string;
  current_period_end: string;
  session_id: string;
  email: string;
  cancel_at_period_end?: boolean;
  cancelled_at?: string | null;
}

export interface SubscriptionUpdate {
  customer_id?: string;
  subscription_id?: string;
  quiz_id?: number;
  status?: string;
  current_period_start?: string;
  current_period_end?: string;
  session_id?: string;
  email?: string;
  cancel_at_period_end?: boolean;
  cancelled_at?: string | null;
}

// List subscriptions with optional filtering
export const useSubscriptions = (filters?: {
  customer_id?: string;
  subscription_id?: string;
  quiz_id?: number;
  status?: string;
  email?: string;
  limit?: number;
  offset?: number;
  include_lead_details?: boolean;
}, options?: { enabled?: boolean }) => {
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ['subscriptions', filters],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      console.log('🔍 useSubscriptions called with filters:', filters);
      console.log('🔍 useSubscriptions client:', client);
      
      if (!client) throw new Error("API client not initialized");
      
      const params = new URLSearchParams();
      if (filters?.customer_id) params.append('customer_id', filters.customer_id);
      if (filters?.subscription_id) params.append('subscription_id', filters.subscription_id);
      if (filters?.quiz_id) params.append('quiz_id', filters.quiz_id.toString());
      if (filters?.status) params.append('status', filters.status);
      if (filters?.email) params.append('email', filters.email);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.offset) params.append('offset', filters.offset.toString());
      if (filters?.include_lead_details !== undefined) params.append('include_lead_details', filters.include_lead_details.toString());
      
      const url = `/subscriptions?${params.toString()}`;
      console.log('🔍 useSubscriptions making request to:', url);
      
      try {
        const response = await client.get(url);
        console.log('🔍 useSubscriptions API response:', response);
        console.log('🔍 useSubscriptions response.data:', response.data);
        return response.data as Subscription[];
      } catch (error) {
        console.error('❌ useSubscriptions API error:', error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Get a specific subscription by ID
export const useSubscription = (subscriptionId: number, includeLeadDetails: boolean = true) => {
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ['subscription', subscriptionId, includeLeadDetails],
    queryFn: async () => {
      if (!client) throw new Error("API client not initialized");
      
      const params = new URLSearchParams();
      if (includeLeadDetails !== undefined) params.append('include_lead_details', includeLeadDetails.toString());
      
      const response = await client.get(`/subscriptions/${subscriptionId}?${params.toString()}`);
      return response.data as Subscription;
    },
    enabled: !!subscriptionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Create a new subscription
export const useCreateSubscription = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (subscriptionData: SubscriptionCreate) => {
      if (!client) throw new Error("API client not initialized");
      
      const response = await client.post('/subscriptions', subscriptionData);
      return response.data as Subscription;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    }
  });
};

// Update an existing subscription
export const useUpdateSubscription = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ subscriptionId, subscriptionData }: { 
      subscriptionId: number; 
      subscriptionData: SubscriptionUpdate 
    }) => {
      if (!client) throw new Error("API client not initialized");
      
      const response = await client.put(`/subscriptions/${subscriptionId}`, subscriptionData);
      return response.data as Subscription;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['subscription', variables.subscriptionId] });
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    }
  });
};

// Delete a subscription
export const useDeleteSubscription = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (subscriptionId: number) => {
      if (!client) throw new Error("API client not initialized");
      
      const response = await client.delete(`/subscriptions/${subscriptionId}`);
      return response.data;
    },
    onSuccess: (data, subscriptionId) => {
      queryClient.invalidateQueries({ queryKey: ['subscription', subscriptionId] });
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    }
  });
};

// Get subscriptions by customer ID
export const useSubscriptionsByCustomer = (customerId: string, includeLeadDetails: boolean = true) => {
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ['subscriptionsByCustomer', customerId, includeLeadDetails],
    queryFn: async () => {
      if (!client) throw new Error("API client not initialized");
      
      const params = new URLSearchParams();
      if (includeLeadDetails !== undefined) params.append('include_lead_details', includeLeadDetails.toString());
      
      const response = await client.get(`/subscriptions/customer/${customerId}?${params.toString()}`);
      return response.data as Subscription[];
    },
    enabled: !!customerId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Get subscriptions by quiz ID
export const useSubscriptionsByQuiz = (quizId: number, includeLeadDetails: boolean = true) => {
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ['subscriptionsByQuiz', quizId, includeLeadDetails],
    queryFn: async () => {
      if (!client) throw new Error("API client not initialized");
      
      const params = new URLSearchParams();
      if (includeLeadDetails !== undefined) params.append('include_lead_details', includeLeadDetails.toString());
      
      const response = await client.get(`/subscriptions/quiz/${quizId}?${params.toString()}`);
      return response.data as Subscription[];
    },
    enabled: !!quizId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Update subscription with profile
export const useUpdateSubscriptionWithProfile = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ subscriptionId, profile }: { subscriptionId: string; profile: string }) => {
      if (!client) throw new Error("API client not initialized");
      
      const response = await client.put(`/subscriptions/${subscriptionId}/update/profile`, {
        profile
      });
      return response.data as Subscription;
    },
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    },
  });
};

// Get subscriptions by profile
export const useSubscriptionsByProfile = (profileId: string) => {
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ['subscriptionsByProfile', profileId],
    queryFn: async () => {
      if (!client) throw new Error("API client not initialized");
      
      const response = await client.get(`/subscriptions/profile/${profileId}`);
      return response.data as Subscription[];
    },
    enabled: !!profileId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Cancel a subscription
export const useCancelSubscription = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (subscriptionId: number) => {
      if (!client) throw new Error("API client not initialized");
      
      const response = await client.put(`/subscriptions/${subscriptionId}/cancel`);
      return response.data as Subscription;
    },
    onSuccess: (data, subscriptionId) => {
      queryClient.invalidateQueries({ queryKey: ['subscription', subscriptionId] });
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['subscriptionsByCustomer'] });
      queryClient.invalidateQueries({ queryKey: ['subscriptionsByQuiz'] });
    }
  });
};

// Utility functions for subscription status
export const isSubscriptionActive = (subscription: Subscription): boolean => {
  if (!subscription) return false;
  
  // Check if subscription is cancelled
  if (subscription.cancelled_at) return false;
  
  // Check if subscription is set to cancel at period end
  if (subscription.cancel_at_period_end) return false;
  
  // Check if current period has ended
  if (!subscription.current_period_end) return false;
  
  const now = new Date();
  const periodEnd = new Date(subscription.current_period_end);
  
  return now <= periodEnd;
};

export const getSubscriptionStatus = (subscription: Subscription): string => {
  if (!subscription) return 'unknown';
  
  if (subscription.cancelled_at) return 'cancelled';
  if (subscription.cancel_at_period_end) return 'cancelling';
  
  if (!subscription.current_period_end) return 'unknown';
  
  const now = new Date();
  const periodEnd = new Date(subscription.current_period_end);
  
  if (now > periodEnd) return 'expired';
  return subscription.status || 'active';
};

// Helper function to format subscription dates
export const formatSubscriptionDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Helper function to get time until subscription expires
export const getTimeUntilExpiry = (subscription: Subscription): string => {
  if (!subscription || !subscription.current_period_end) return 'Unknown';
  
  const now = new Date();
  const periodEnd = new Date(subscription.current_period_end);
  const timeDiff = periodEnd.getTime() - now.getTime();
  
  if (timeDiff <= 0) return 'Expired';
  
  const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  
  if (days > 0) {
    return `${days} day${days !== 1 ? 's' : ''} remaining`;
  } else if (hours > 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''} remaining`;
  } else {
    return 'Less than 1 hour remaining';
  }
};

// Helper function to get full name from lead details
export const getLeadFullName = (subscription: Subscription): string => {
  if (!subscription?.lead_details) return 'Unknown';
  
  const { first_name, last_name } = subscription.lead_details;
  if (first_name && last_name) {
    return `${first_name} ${last_name}`;
  } else if (first_name) {
    return first_name;
  } else if (last_name) {
    return last_name;
  }
  
  return 'Unknown';
};

// Helper function to get lead email (prefer lead details, fallback to subscription email)
export const getLeadEmail = (subscription: Subscription): string => {
  if (subscription?.lead_details?.email) {
    return subscription.lead_details.email;
  }
  return subscription?.email || 'Unknown';
};

// Helper function to get lead phone
export const getLeadPhone = (subscription: Subscription): string => {
  return subscription?.lead_details?.phone || 'Not provided';
};

// Helper function to check if subscription has lead details
export const hasLeadDetails = (subscription: Subscription): boolean => {
  return !!(subscription?.lead_details?.first_name || subscription?.lead_details?.last_name);
}; 